import { Share, MoreVertical, Plus, Lock, RotateCw, Bell, Download, Home, Star, Search, Compass, Chrome } from 'lucide-react';

const APP_ICON = '/icon-192.png';

export function Pulse({ className = '' }) {
  return (
    <span className={`absolute pointer-events-none ${className}`}>
      <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping print:hidden" />
      <span className="absolute inset-0 rounded-full ring-2 ring-red-500" />
    </span>
  );
}

export function PhoneFrame({ os, children, caption }) {
  const notch = os === 'ios'
    ? <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-14 h-4 bg-[#0B1120] rounded-full z-10" />
    : <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#0B1120] rounded-full z-10" />;
  return (
    <figure className="flex flex-col items-center gap-2 shrink-0 break-inside-avoid">
      <div className={`relative w-[156px] h-[300px] bg-[#0B1120] p-[5px] shadow-xl shadow-slate-900/15 ${os === 'ios' ? 'rounded-[30px]' : 'rounded-[22px]'}`}>
        <div className={`relative w-full h-full overflow-hidden bg-white ${os === 'ios' ? 'rounded-[25px]' : 'rounded-[17px]'}`}>
          {notch}
          <div className="absolute inset-0 pt-6 flex flex-col text-[8px] font-ibm text-slate-700">{children}</div>
        </div>
      </div>
      {caption && <figcaption className="text-[11px] text-slate-400 font-ibm text-center max-w-[160px]">{caption}</figcaption>}
    </figure>
  );
}

function AppIcon({ label = 'Vapelarm', size = 'w-9 h-9', highlight }) {
  return (
    <div className="relative flex flex-col items-center gap-0.5">
      <img src={APP_ICON} alt="" className={`${size} rounded-[22%]`} />
      <span className="text-[7px] text-white font-medium">{label}</span>
      {highlight && <Pulse className="-inset-1.5 rounded-xl" />}
    </div>
  );
}

function Tile({ color, icon: Icon, label, highlight }) {
  return (
    <div className="relative flex flex-col items-center gap-0.5">
      <div className={`w-9 h-9 rounded-[22%] flex items-center justify-center ${color}`}>
        {Icon && <Icon className="h-4 w-4 text-white" />}
      </div>
      <span className="text-[7px] text-white/90">{label}</span>
      {highlight && <Pulse className="-inset-1.5" />}
    </div>
  );
}

export function HomeScreen({ os, highlight }) {
  const bg = os === 'ios' ? 'bg-gradient-to-b from-sky-700 to-indigo-900' : 'bg-gradient-to-b from-emerald-800 to-slate-900';
  return (
    <div className={`flex-1 ${bg} px-3 pt-4 grid grid-cols-4 gap-y-3 content-start`}>
      <Tile color="bg-slate-500/60" label="Kamera" />
      <Tile color="bg-slate-500/60" label="Foton" />
      <Tile color="bg-slate-500/60" label="Kalender" />
      {highlight === 'app' ? <AppIcon highlight /> : <Tile color="bg-slate-500/60" label="Klocka" />}
      <Tile color="bg-slate-500/60" label="Väder" />
      <Tile color="bg-slate-500/60" label="Kartor" />
      <Tile color="bg-slate-500/60" label="Mejl" />
      <Tile color="bg-slate-500/60" label="Musik" />
      <div className="col-span-4 flex justify-around bg-white/15 rounded-2xl py-1.5 mt-14">
        {os === 'ios'
          ? <Tile color="bg-gradient-to-b from-sky-400 to-blue-600" icon={Compass} label="Safari" highlight={highlight === 'browser'} />
          : <Tile color="bg-gradient-to-br from-red-500 via-amber-400 to-green-500" icon={Chrome} label="Chrome" highlight={highlight === 'browser'} />}
        <Tile color="bg-emerald-500" label="Telefon" />
        <Tile color="bg-blue-500" label="Meddel." />
      </div>
    </div>
  );
}

function PageBody() {
  return (
    <div className="flex-1 px-3 pt-3 space-y-1.5 bg-slate-50">
      <p className="font-chivo font-black text-[11px] text-navy text-center">Vapelarm Cloud</p>
      <div className="bg-white rounded border border-slate-200 p-2 space-y-1.5">
        <div className="h-3 rounded-sm bg-slate-100 border border-slate-200" />
        <div className="h-3 rounded-sm bg-slate-100 border border-slate-200" />
        <div className="h-3.5 rounded-sm bg-navy" />
      </div>
    </div>
  );
}

export function SafariScreen({ highlight, url = true }) {
  return (
    <>
      <PageBody />
      <div className="bg-slate-100/95 border-t border-slate-200 px-2 pt-1.5 pb-3 space-y-1.5">
        <div className={`relative flex items-center gap-1 bg-white rounded-lg px-2 py-1 shadow-sm ${highlight === 'url' ? 'ring-2 ring-red-500' : ''}`}>
          <Lock className="h-2 w-2 text-slate-400" />
          <span className="flex-1 text-center">{url ? 'cloud.vapelarm.se' : 'Sök eller ange adress'}</span>
          <RotateCw className="h-2 w-2 text-slate-400" />
        </div>
        <div className="flex justify-between px-2 text-blue-500">
          <span>‹</span><span>›</span>
          <span className="relative">
            <Share className="h-3 w-3" />
            {highlight === 'share' && <Pulse className="-inset-1.5" />}
          </span>
          <span>□</span>
          <span className="relative font-bold">
            •••
            {highlight === 'more' && <Pulse className="-inset-1.5" />}
          </span>
        </div>
      </div>
    </>
  );
}

function SheetRow({ icon: Icon, label, highlight }) {
  return (
    <div className={`relative flex items-center justify-between px-2.5 py-1.5 bg-white ${highlight ? 'z-10' : ''}`}>
      <span className={highlight ? 'font-semibold text-slate-900' : ''}>{label}</span>
      <Icon className="h-2.5 w-2.5 text-slate-500" />
      {highlight && <span className="absolute inset-0 ring-2 ring-red-500 rounded" />}
    </div>
  );
}

export function IosShareSheet() {
  return (
    <div className="flex-1 bg-slate-900/40 flex flex-col justify-end">
      <div className="bg-slate-100 rounded-t-2xl p-2 space-y-1.5">
        <div className="flex items-center gap-1.5 bg-white rounded-lg p-1.5">
          <img src={APP_ICON} alt="" className="w-5 h-5 rounded" />
          <div><p className="font-semibold">Vapelarm Cloud</p><p className="text-slate-400">cloud.vapelarm.se</p></div>
        </div>
        <div className="rounded-lg overflow-hidden divide-y divide-slate-100">
          <SheetRow icon={Star} label="Lägg till bokmärke" />
          <SheetRow icon={Search} label="Hitta på sidan" />
          <SheetRow icon={Plus} label="Lägg till på hemskärmen" highlight />
        </div>
      </div>
    </div>
  );
}

export function IosAddScreen() {
  return (
    <div className="flex-1 bg-slate-100 flex flex-col">
      <div className="flex justify-between items-center px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <span className="text-blue-500 text-[7px]">Avbryt</span>
        <span className="font-semibold text-[6px] whitespace-nowrap">Lägg till på hemskärmen</span>
        <span className="relative text-blue-500 font-semibold">Lägg till<Pulse className="-inset-x-1.5 -inset-y-1" /></span>
      </div>
      <div className="m-2 bg-white rounded-lg p-2 flex items-center gap-2">
        <img src={APP_ICON} alt="" className="w-8 h-8 rounded-lg" />
        <div className="flex-1 border-b border-slate-100 pb-1">
          <p className="font-semibold">Vapelarm</p>
          <p className="text-slate-400">cloud.vapelarm.se</p>
        </div>
      </div>
      <div className="mx-2 bg-white rounded-lg px-2 py-1.5 flex items-center justify-between">
        <span>Öppna som webbapp</span>
        <span className="w-5 h-3 rounded-full bg-emerald-500 relative"><span className="absolute right-0.5 top-0.5 w-2 h-2 rounded-full bg-white" /></span>
      </div>
      <p className="mx-3 mt-1 text-[7px] text-slate-400">Visas i iOS 26 och senare – ska vara påslagen.</p>
    </div>
  );
}

export function ChromeScreen({ menu, highlight }) {
  return (
    <>
      <div className="flex items-center gap-1 px-2 py-1.5 bg-white border-b border-slate-200">
        <Home className="h-2.5 w-2.5 text-slate-500" />
        <div className={`flex-1 flex items-center gap-1 bg-slate-100 rounded-full px-2 py-1 ${highlight === 'url' ? 'ring-2 ring-red-500' : ''}`}>
          <Lock className="h-2 w-2 text-slate-400" />cloud.vapelarm.se
        </div>
        <span className="relative">
          <MoreVertical className="h-3 w-3 text-slate-600" />
          {highlight === 'more' && <Pulse className="-inset-1.5" />}
        </span>
      </div>
      <div className="relative flex-1 flex flex-col">
        <PageBody />
        {menu && (
          <div className="absolute right-1 top-0 w-[110px] bg-white rounded-md shadow-xl border border-slate-200 py-1">
            {['Ny flik', 'Historik', 'Nedladdningar', 'Bokmärken'].map(l => <p key={l} className="px-2 py-1">{l}</p>)}
            <div className="relative px-2 py-1 font-semibold text-slate-900 flex items-center gap-1">
              <Download className="h-2.5 w-2.5" /> Installera app
              <span className="absolute inset-0 ring-2 ring-red-500 rounded" />
            </div>
            <p className="px-2 py-1">Inställningar</p>
          </div>
        )}
      </div>
    </>
  );
}

export function AndroidInstallDialog() {
  return (
    <div className="flex-1 bg-slate-900/50 flex items-center justify-center px-3">
      <div className="bg-white rounded-2xl p-3 w-full space-y-2 shadow-xl">
        <p className="font-semibold text-[9px] text-slate-900">Installera app</p>
        <div className="flex items-center gap-2">
          <img src={APP_ICON} alt="" className="w-7 h-7 rounded-lg" />
          <div><p className="font-semibold">Vapelarm</p><p className="text-slate-400">cloud.vapelarm.se</p></div>
        </div>
        <div className="flex justify-end gap-3 pt-1 text-emerald-700 font-semibold">
          <span>Avbryt</span>
          <span className="relative">Installera<Pulse className="-inset-x-1.5 -inset-y-1" /></span>
        </div>
      </div>
    </div>
  );
}

export function LoginScreen() {
  return (
    <div className="flex-1 bg-slate-50 px-3 pt-6 space-y-2">
      <p className="font-chivo font-black text-[12px] text-navy text-center">Vapelarm Cloud</p>
      <div className="bg-white border border-slate-200 rounded p-2 space-y-1.5">
        <p className="uppercase tracking-widest text-[6px] text-slate-500">E-post</p>
        <div className="h-3.5 rounded-sm border border-slate-200 px-1 text-slate-400 flex items-center">namn@skola.se</div>
        <p className="uppercase tracking-widest text-[6px] text-slate-500">Lösenord</p>
        <div className="h-3.5 rounded-sm border border-slate-200 px-1 text-slate-400 flex items-center">••••••••</div>
        <div className="relative h-4 rounded-sm bg-navy text-white flex items-center justify-center font-semibold">
          Logga in<Pulse className="-inset-1 rounded" />
        </div>
      </div>
    </div>
  );
}

export function PushBannerScreen() {
  return (
    <div className="flex-1 flex flex-col bg-slate-50">
      <div className="bg-navy text-white px-2 py-1.5 flex items-center gap-1.5">
        <Bell className="h-2.5 w-2.5 shrink-0" />
        <span className="flex-1 leading-tight">Aktivera push-notiser för att få larm även när appen är stängd</span>
        <span className="relative bg-white text-navy px-1.5 py-0.5 rounded-sm font-bold">Aktivera<Pulse className="-inset-1 rounded" /></span>
      </div>
      <div className="p-2 space-y-1.5">
        <div className="h-8 rounded bg-white border border-slate-200" />
        <div className="h-8 rounded bg-white border border-slate-200" />
      </div>
    </div>
  );
}

export function PermissionDialog({ os }) {
  const ios = os === 'ios';
  return (
    <div className="flex-1 bg-slate-900/50 flex items-center justify-center px-3">
      <div className={`bg-white/95 w-full text-center shadow-xl ${ios ? 'rounded-xl' : 'rounded-2xl p-3'}`}>
        <div className={ios ? 'p-2.5' : ''}>
          <Bell className="h-3.5 w-3.5 mx-auto text-slate-600 mb-1" />
          <p className="font-semibold text-slate-900">
            {ios ? '”Vapelarm” vill skicka notiser till dig' : 'Vill du tillåta att Vapelarm skickar aviseringar?'}
          </p>
        </div>
        <div className={ios ? 'grid grid-cols-2 border-t border-slate-200 text-blue-500' : 'flex flex-col gap-1 mt-2 text-emerald-700 font-semibold'}>
          {ios ? (
            <>
              <span className="py-1.5 border-r border-slate-200">Tillåt inte</span>
              <span className="relative py-1.5 font-semibold">Tillåt<Pulse className="inset-0.5 rounded" /></span>
            </>
          ) : (
            <>
              <span className="relative py-1">Tillåt<Pulse className="inset-0 rounded" /></span>
              <span className="py-1">Tillåt inte</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
